import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import DogNameInput from "../../components/form/DogName.input";
import EamilInput from "../../components/form/Email.input";
import PasswordInput from "../../components/form/Password.input";
import Header from "../../components/Header.component";
import Popup from "../../components/Popup.component";
import { LockIcon } from "../../components/svgs/icons/Lock.icon";
import { PlaneIcon } from "../../components/svgs/icons/Plane.icon";
import { storageKey } from "../../configs/config";
import { theme } from "../../configs/theme";
import api, { apiErrors, ErrorResponse } from "../../services/api";
import { setMixpanelUserId } from "../../services/mixpanel";
import { TertiaryFitContentButton } from "../../styles/Button.style";
import { RootContainer } from "../../styles/Container.style";
import { ErrorMessage, FormLabel, StyledForm } from "../../styles/Form";
import { Column, Gap } from "../../styles/Utils";
import { ImageContainer } from "./Landing.page";

interface FormData {
  email: string;
  dogName: string;
  password: string;
}

export default function LogInPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { email, dogName } = location.state || {};

  useEffect(() => {
    if (!email || !dogName) navigate(routeList.landing);
  });

  const [resetPassPopupVisible, setResetPassPopupVisible] = useState(false);
  const [wrongPassPopupVisible, setWrongPassPopupVisible] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      email: email || "",
      dogName: dogName || "",
    },
  });

  const emailnputValue = watch("email");

  const onSubmit = async (data: FormData) => {
    try {
      const response = await api.logIn(data);
      localStorage.setItem(storageKey.accessToken, response.data.accessToken); // Save JWT token
      localStorage.setItem(storageKey.refreshToken, response.data.refreshToken);
      setMixpanelUserId(response.data.accessToken);
      navigate(routeList.addPictures);
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      if (axiosError.response?.data.error === apiErrors.INVALID_CREDENTIAL) {
        setWrongPassPopupVisible(true);
      } else {
        console.log("Error logging user", err);
        alert("Error logging user");
      }
    }
  };

  const resetPassword = () => {
    if (emailnputValue) {
      try {
        api.requestPasswordReset({ email: emailnputValue, dogName });
        setResetPassPopupVisible(true);
      } catch (err) {
        console.log("Error requesting password reset", err);
        alert("Error requesting password reset");
      }
    }
  };

  return (
    <RootContainer $background={theme.color.primary} $topSafe={true}>
      {resetPassPopupVisible && (
        <Popup
          type="info"
          title={lang.logInPage.reset_password_popup.title}
          message={lang.logInPage.reset_password_popup.message}
          buttonText={lang.logInPage.reset_password_popup.buttonText}
          Icon={PlaneIcon}
          onClose={() => setResetPassPopupVisible(false)}
          action={() => setResetPassPopupVisible(false)}
        />
      )}
      {wrongPassPopupVisible && (
        <Popup
          type="error"
          title={lang.logInPage.wrong_password_popup.title}
          message={lang.logInPage.wrong_password_popup.message}
          buttonText={lang.logInPage.wrong_password_popup.buttonText}
          Icon={LockIcon}
          onClose={() => setWrongPassPopupVisible(false)}
          action={() => setWrongPassPopupVisible(false)}
        />
      )}
      <Column $alignItem="center" $maxHeight>
        <Header sloganImgSrc="/slogans/slogan-white.png" />
        <ImageContainer>
          <img src="/illustrations/master-dog-dance.png" />
        </ImageContainer>{" "}
        <Gap $height={2} />
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <EamilInput register={register} name="email" disabled />
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          <Gap $height={1} />
          <DogNameInput register={register} name="dogName" disabled />
          {errors.dogName && <ErrorMessage>{errors.dogName.message}</ErrorMessage>}
          <Gap $height={1} />
          <PasswordInput register={register} name="password" />
          {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          <Gap $height={0.5} />
          <ForgetPassWrapper onClick={resetPassword}>
            {lang.logInPage.forget_password} <u>{lang.logInPage.buttons.send_reset_link}</u>
          </ForgetPassWrapper>
          <Gap $height={1.5} />
          <TertiaryFitContentButton type="submit" disabled={!isValid}>
            <span>{lang.logInPage.buttons.log_in}</span>
          </TertiaryFitContentButton>
        </StyledForm>
      </Column>
    </RootContainer>
  );
}

const ForgetPassWrapper = styled(FormLabel)`
  color: ${(props) => props.theme.color.primaryDark};
  > u {
    color: ${(props) => props.theme.color.tertiary};
    cursor: pointer;
  }
`;
